// src/components/admin/AdminSidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiGrid,
  FiBarChart2,
  FiSettings,
  FiLogOut,
  FiChevronRight,
  FiChevronDown,
  FiUsers,
  FiMail,
  FiSun,
  FiMoon,
  FiMonitor,
  FiPackage,
  FiCalendar,
  FiBell,
  FiBookOpen,
} from 'react-icons/fi';
import { BiHelpCircle } from 'react-icons/bi';
import { FaRegFaceGrinStars } from 'react-icons/fa6';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  db,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from '../../services/firebase';

// Import brand logos
import LogoIconBlack from '../../assets/Images/Default/wd-hejbul-official-brand-logo-icon-black-version.png';
import LogoIconWhite from '../../assets/Images/Default/wd-hejbul-official-brand-logo-icon-white-version.png';

const menuItems = [
  {
    id: 'dashboard',
    icon: FiHome,
    label: 'Dashboard',
    path: '/admin/dashboard',
    notificationKey: null,
  },
  {
    id: 'blog',
    icon: FiBookOpen,
    label: 'Blog',
    path: '/admin/blog',
    notificationKey: 'blog_engagement',
  },
  {
    id: 'portfolio',
    icon: FiGrid,
    label: 'Portfolio',
    path: '/admin/portfolio',
    notificationKey: 'portfolio_engagement',
  },
  {
    id: 'readyMadeServices',
    icon: FiPackage,
    label: 'Ready-made Services',
    path: '/admin/ready-made-services',
    notificationKey: null,
  },
  {
    id: 'subscriptionPlans',
    icon: FiCalendar,
    label: 'Subscription Plans',
    path: '/admin/subscription-plans',
    notificationKey: null,
  },
  {
    id: 'inquiries',
    icon: BiHelpCircle,
    label: 'Inquiries',
    notificationKey: 'total_inquiries',
    submenu: [
      {
        id: 'hire',
        icon: FiUsers,
        label: 'Hire Inquiries',
        path: '/admin/inquiries/hire',
        notificationKey: 'hire_inquiries',
      },
      {
        id: 'ready-made',
        icon: FiPackage,
        label: 'Ready-made Inquiries',
        path: '/admin/inquiries/ready-made',
        notificationKey: 'ready_made_inquiries',
      },
      {
        id: 'subscription',
        icon: FiCalendar,
        label: 'Subscription Inquiries',
        path: '/admin/inquiries/subscription',
        notificationKey: 'subscription_inquiries',
      },
      {
        id: 'contact',
        icon: FiMail,
        label: 'Contact Messages',
        path: '/admin/inquiries/contact',
        notificationKey: 'contact_messages',
      },
    ],
  },
  {
    id: 'reviews',
    icon: FaRegFaceGrinStars,
    label: 'Reviews',
    path: '/admin/reviews',
    notificationKey: 'reviews',
  },
  {
    id: 'analytics',
    icon: FiBarChart2,
    label: 'Analytics',
    path: '/admin/analytics',
    notificationKey: null,
  },
  {
    id: 'settings',
    icon: FiSettings,
    label: 'Settings',
    path: '/admin/settings',
    notificationKey: null,
  },
];

export default function AdminSidebar({ isCollapsed, onClose }) {
  const [openMenus, setOpenMenus] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0 });
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({
    hire_inquiries: 0,
    ready_made_inquiries: 0,
    subscription_inquiries: 0,
    contact_messages: 0,
    reviews: 0,
    portfolio_engagement: 0,
    blog_engagement: 0,
    total_inquiries: 0,
  });

  const themeButtonRef = useRef(null);
  const themeDropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { resolvedTheme, theme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const getLogo = () => {
    if (isCollapsed) {
      return isDark ? LogoIconWhite : LogoIconBlack;
    }
    return isDark
      ? 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1784030465/wd-hejbul-official-brand-logo-white-version_xvqyeo.png'
      : 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1784030465/wd-hejbul-official-brand-logo-black-version_gaippc.png';
  };

  // Fetch notification counts from Firestore
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      try {
        const notificationsRef = collection(db, 'notifications');

        // Get counts for each type (unread only)
        const hireQuery = query(
          notificationsRef,
          where('type', '==', 'hire-inquiry'),
          where('read', '==', false),
        );
        const readyMadeQuery = query(
          notificationsRef,
          where('type', '==', 'ready-made-inquiry'),
          where('read', '==', false),
        );
        const subscriptionQuery = query(
          notificationsRef,
          where('type', '==', 'subscription-inquiry'),
          where('read', '==', false),
        );
        const contactQuery = query(
          notificationsRef,
          where('type', '==', 'contact-message'),
          where('read', '==', false),
        );
        const reviewsQuery = query(
          notificationsRef,
          where('type', '==', 'review'),
          where('read', '==', false),
        );

        // Portfolio engagement notifications (likes and shares)
        const portfolioLikeQuery = query(
          notificationsRef,
          where('type', '==', 'portfolio-like'),
          where('read', '==', false),
        );
        const portfolioShareQuery = query(
          notificationsRef,
          where('type', '==', 'portfolio-share'),
          where('read', '==', false),
        );

        // Blog engagement notifications
        const blogCommentQuery = query(
          notificationsRef,
          where('type', '==', 'blog-comment'),
          where('read', '==', false),
        );
        const blogReplyQuery = query(
          notificationsRef,
          where('type', '==', 'blog-reply'),
          where('read', '==', false),
        );
        const blogReactionQuery = query(
          notificationsRef,
          where('type', '==', 'blog-reaction'),
          where('read', '==', false),
        );

        const [
          hireSnap,
          readyMadeSnap,
          subscriptionSnap,
          contactSnap,
          reviewsSnap,
          portfolioLikeSnap,
          portfolioShareSnap,
          blogCommentSnap,
          blogReplySnap,
          blogReactionSnap,
        ] = await Promise.all([
          getDocs(hireQuery),
          getDocs(readyMadeQuery),
          getDocs(subscriptionQuery),
          getDocs(contactQuery),
          getDocs(reviewsQuery),
          getDocs(portfolioLikeQuery),
          getDocs(portfolioShareQuery),
          getDocs(blogCommentQuery),
          getDocs(blogReplyQuery),
          getDocs(blogReactionQuery),
        ]);

        const hireCount = hireSnap.size;
        const readyMadeCount = readyMadeSnap.size;
        const subscriptionCount = subscriptionSnap.size;
        const contactCount = contactSnap.size;
        const reviewsCount = reviewsSnap.size;
        const portfolioCount = portfolioLikeSnap.size + portfolioShareSnap.size;
        const blogEngagementCount =
          blogCommentSnap.size + blogReplySnap.size + blogReactionSnap.size;

        // Total inquiries count excludes portfolio and blog engagement
        const totalInquiriesCount =
          hireCount + readyMadeCount + subscriptionCount + contactCount;

        setNotificationCounts({
          hire_inquiries: hireCount,
          ready_made_inquiries: readyMadeCount,
          subscription_inquiries: subscriptionCount,
          contact_messages: contactCount,
          reviews: reviewsCount,
          portfolio_engagement: portfolioCount,
          blog_engagement: blogEngagementCount,
          total_inquiries: totalInquiriesCount,
        });
      } catch (error) {
        console.error('Error fetching notification counts:', error);
      }
    };

    fetchNotificationCounts();

    // Set up real-time listener for notifications to update counts
    const unsubscribe = onSnapshot(collection(db, 'notifications'), () => {
      fetchNotificationCounts();
    });

    return () => unsubscribe();
  }, []);

  // Mark notification as read when navigating
  const handleNavigation = async (path, notificationKey) => {
    if (notificationKey && notificationCounts[notificationKey] > 0) {
      try {
        const notificationsRef = collection(db, 'notifications');
        let typeFilter;

        switch (notificationKey) {
          case 'hire_inquiries':
            typeFilter = 'hire-inquiry';
            break;
          case 'ready_made_inquiries':
            typeFilter = 'ready-made-inquiry';
            break;
          case 'subscription_inquiries':
            typeFilter = 'subscription-inquiry';
            break;
          case 'contact_messages':
            typeFilter = 'contact-message';
            break;
          case 'reviews':
            typeFilter = 'review';
            break;
          case 'portfolio_engagement':
            typeFilter = ['portfolio-like', 'portfolio-share'];
            break;
          case 'blog_engagement':
            typeFilter = ['blog-comment', 'blog-reply', 'blog-reaction'];
            break;
          default:
            return;
        }

        let snapshot;
        if (Array.isArray(typeFilter)) {
          const q = query(
            notificationsRef,
            where('type', 'in', typeFilter),
            where('read', '==', false),
          );
          snapshot = await getDocs(q);
        } else {
          const q = query(
            notificationsRef,
            where('type', '==', typeFilter),
            where('read', '==', false),
          );
          snapshot = await getDocs(q);
        }

        const batch = db.batch();
        snapshot.forEach(doc => {
          batch.update(doc.ref, { read: true, readAt: new Date() });
        });
        await batch.commit();

        // Update local counts
        setNotificationCounts(prev => ({
          ...prev,
          [notificationKey]: 0,
          total_inquiries:
            notificationKey === 'portfolio_engagement' ||
            notificationKey === 'blog_engagement'
              ? prev.total_inquiries
              : Math.max(
                  0,
                  prev.total_inquiries - (prev[notificationKey] || 0),
                ),
        }));
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }

    onClose();
  };

  // Handle blog navigation with highlight
  const handleBlogNavigation = () => {
    // Mark blog notifications as read
    handleNavigation('/admin/blog', 'blog_engagement');
    // Navigate to blog
    navigate('/admin/blog');
    onClose();
  };

  // Close theme dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        isThemeDropdownOpen &&
        themeButtonRef.current &&
        !themeButtonRef.current.contains(event.target) &&
        themeDropdownRef.current &&
        !themeDropdownRef.current.contains(event.target)
      ) {
        setIsThemeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isThemeDropdownOpen]);

  // Auto-expand menu based on current route
  useEffect(() => {
    menuItems.forEach(item => {
      if (item.submenu) {
        const isActive = item.submenu.some(
          sub => location.pathname === sub.path,
        );
        if (isActive && !openMenus[item.id]) {
          setOpenMenus(prev => ({ ...prev, [item.id]: true }));
        }
      }
    });
  }, [location.pathname]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showLogoutModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLogoutModal]);

  const toggleSubmenu = (id, e) => {
    e.stopPropagation();
    setOpenMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleMouseEnter = (id, e) => {
    if (isCollapsed) {
      setHoveredItem(id);
      const rect = e.currentTarget.getBoundingClientRect();
      setSubmenuPosition({ top: rect.top + rect.height / 2 });
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setHoveredItem(null);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    window.location.href = '/wdalogin';
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleThemeChange = newTheme => {
    setTheme(newTheme);
    setTimeout(() => {
      setIsThemeDropdownOpen(false);
    }, 300);
  };

  const toggleThemeDropdown = e => {
    e.stopPropagation();
    setIsThemeDropdownOpen(!isThemeDropdownOpen);
  };

  const getThemeIcon = () => {
    if (theme === 'light') return <FiSun className="w-5 h-5" />;
    if (theme === 'dark') return <FiMoon className="w-5 h-5" />;
    return <FiMonitor className="w-5 h-5" />;
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: FiSun },
    { value: 'dark', label: 'Dark', icon: FiMoon },
    { value: 'system', label: 'System', icon: FiMonitor },
  ];

  const getThemeText = () => {
    if (theme === 'light') return 'Light';
    if (theme === 'dark') return 'Dark';
    return 'System';
  };

  const renderThemeDropdown = () => {
    if (!isThemeDropdownOpen) return null;

    return createPortal(
      <div
        ref={themeDropdownRef}
        className="fixed z-[9999] animate-fade-in-up"
        style={{
          bottom: isCollapsed ? '70px' : '80px',
          left: isCollapsed ? '70px' : '220px',
        }}
      >
        <div
          className="w-44 rounded-xl shadow-2xl overflow-hidden border"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        >
          {themeOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleThemeChange(option.value)}
              className={`w-full flex items-center gap-3 px-4 py-3 transition-all duration-200 ${
                theme === option.value
                  ? 'bg-black/10 dark:bg-white/10'
                  : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              style={{
                color: theme === option.value ? 'var(--text)' : 'var(--muted)',
              }}
            >
              <option.icon className="w-4 h-4" />
              <span className="text-sm">{option.label}</span>
              {theme === option.value && (
                <span className="ml-auto text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
        <div
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border-r border-b"
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
          }}
        />
      </div>,
      document.body,
    );
  };

  const renderLogoutModal = () => {
    if (!showLogoutModal) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
          onClick={handleCancelLogout}
        />
        <div className="relative w-full max-w-md mx-4 animate-scale-in">
          <div
            className="rounded-2xl shadow-2xl overflow-hidden border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div
              className="p-5 border-b text-center"
              style={{ borderBottomColor: 'var(--border)' }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiLogOut
                  className="w-7 h-7"
                  style={{ color: 'var(--text)' }}
                />
              </div>
              <h3
                className="text-xl font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Logout Confirmation
              </h3>
              <p className="text-sm mt-2" style={{ color: 'var(--muted)' }}>
                Are you sure you want to logout? You'll need to sign in again to
                access the admin dashboard.
              </p>
            </div>
            <div className="p-5 flex gap-3">
              <button
                onClick={handleConfirmLogout}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:opacity-80"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Yes, Logout
              </button>
              <button
                onClick={handleCancelLogout}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const renderNotificationBadge = count => {
    if (!count || count === 0) return null;
    return (
      <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white">
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <div
      className="h-full flex flex-col overflow-hidden"
      style={{
        backgroundColor: 'var(--bg)',
        borderRight: '1px solid var(--border)',
      }}
    >
      {/* Logo Area */}
      <div
        className={`h-16 flex-shrink-0 flex items-center ${isCollapsed ? 'justify-center' : 'px-6'} border-b`}
        style={{ borderBottomColor: 'var(--border)' }}
      >
        <img
          src={getLogo()}
          alt="WD Hajbul Brand Logo"
          className={`object-contain transition-all duration-300 ${
            isCollapsed ? 'w-10 h-10' : 'h-8 w-auto'
          }`}
          key={`logo-${isDark}-${isCollapsed}`}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4">
        <ul className="space-y-1">
          {menuItems.map(item => (
            <li
              key={item.id}
              onMouseEnter={e => handleMouseEnter(item.id, e)}
              onMouseLeave={handleMouseLeave}
            >
              {!item.submenu ? (
                <NavLink
                  to={item.path}
                  onClick={() => {
                    if (item.id === 'blog') {
                      handleBlogNavigation();
                    } else {
                      handleNavigation(item.path, item.notificationKey);
                      onClose();
                    }
                  }}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? 'justify-center' : 'px-4'} py-2.5 mx-2 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-black/10 dark:bg-white/10'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`
                  }
                  style={{ color: 'var(--text)' }}
                  title={isCollapsed ? item.label : ''}
                >
                  <item.icon
                    className={`w-5 h-5 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}
                  />
                  {!isCollapsed && (
                    <>
                      <span className="text-sm font-medium truncate flex-1">
                        {item.label}
                      </span>
                      {item.notificationKey &&
                        renderNotificationBadge(
                          notificationCounts[item.notificationKey],
                        )}
                    </>
                  )}
                </NavLink>
              ) : (
                <>
                  <button
                    onClick={e => !isCollapsed && toggleSubmenu(item.id, e)}
                    className={`w-full flex items-center justify-between ${isCollapsed ? 'justify-center' : 'px-4 mx-2'} py-2.5 rounded-lg transition-all duration-200 ${
                      openMenus[item.id]
                        ? 'bg-black/10 dark:bg-white/10'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                    style={{ color: 'var(--text)' }}
                    title={isCollapsed ? item.label : ''}
                  >
                    <div
                      className={`flex items-center min-w-0 ${isCollapsed ? '' : 'gap-3'}`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {!isCollapsed && (
                        <>
                          <span className="text-sm font-medium truncate flex-1">
                            {item.label}
                          </span>
                          {item.notificationKey &&
                            renderNotificationBadge(
                              notificationCounts[item.notificationKey],
                            )}
                        </>
                      )}
                    </div>
                    {!isCollapsed &&
                      (openMenus[item.id] ? (
                        <FiChevronDown className="w-4 h-4 flex-shrink-0 ml-2" />
                      ) : (
                        <FiChevronRight className="w-4 h-4 flex-shrink-0 ml-2" />
                      ))}
                  </button>

                  {/* Submenu for expanded mode */}
                  {!isCollapsed && openMenus[item.id] && (
                    <ul className="mt-1 ml-8 space-y-1">
                      {item.submenu.map(sub => (
                        <li key={sub.id}>
                          <NavLink
                            to={sub.path}
                            onClick={() =>
                              handleNavigation(sub.path, sub.notificationKey)
                            }
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 ${
                                isActive
                                  ? 'bg-black/10 dark:bg-white/10'
                                  : 'hover:bg-black/5 dark:hover:bg-white/5'
                              }`
                            }
                            style={({ isActive }) => ({
                              color: isActive ? 'var(--text)' : 'var(--muted)',
                            })}
                          >
                            <sub.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm truncate flex-1">
                              {sub.label}
                            </span>
                            {sub.notificationKey &&
                              renderNotificationBadge(
                                notificationCounts[sub.notificationKey],
                              )}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Submenu for collapsed mode */}
                  {isCollapsed &&
                    hoveredItem === item.id &&
                    createPortal(
                      <div
                        className="fixed z-[9998] animate-fade-in-left"
                        style={{
                          top: submenuPosition.top - 60,
                          left: '72px',
                        }}
                      >
                        <div
                          className="w-56 rounded-xl shadow-2xl overflow-hidden border"
                          style={{
                            backgroundColor: 'var(--bg)',
                            borderColor: 'var(--border)',
                          }}
                        >
                          {item.submenu.map(sub => (
                            <NavLink
                              key={sub.id}
                              to={sub.path}
                              onClick={() => {
                                handleNavigation(sub.path, sub.notificationKey);
                                setHoveredItem(null);
                              }}
                              className={({ isActive }) =>
                                `flex items-center justify-between gap-3 px-4 py-2.5 transition-all duration-200 ${
                                  isActive
                                    ? 'bg-black/10 dark:bg-white/10'
                                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                                }`
                              }
                              style={({ isActive }) => ({
                                color: isActive
                                  ? 'var(--text)'
                                  : 'var(--muted)',
                              })}
                            >
                              <div className="flex items-center gap-3">
                                <sub.icon className="w-4 h-4" />
                                <span className="text-sm truncate">
                                  {sub.label}
                                </span>
                              </div>
                              {sub.notificationKey &&
                                renderNotificationBadge(
                                  notificationCounts[sub.notificationKey],
                                )}
                            </NavLink>
                          ))}
                        </div>
                      </div>,
                      document.body,
                    )}
                </>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer Area */}
      <div
        className="flex-shrink-0 p-4 border-t space-y-3"
        style={{ borderTopColor: 'var(--border)' }}
      >
        <div>
          <button
            ref={themeButtonRef}
            onClick={toggleThemeDropdown}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 py-2 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5`}
            style={{ color: 'var(--text)' }}
            title={isCollapsed ? 'Theme' : ''}
          >
            <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
              {getThemeIcon()}
              {!isCollapsed && <span className="text-sm">Theme</span>}
            </div>
            {!isCollapsed && (
              <span
                className="text-xs capitalize"
                style={{ color: 'var(--muted)' }}
              >
                {getThemeText()}
              </span>
            )}
          </button>
          {renderThemeDropdown()}
        </div>

        <button
          onClick={handleLogoutClick}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-4 py-2 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5`}
          style={{ color: 'var(--muted)' }}
          title={isCollapsed ? 'Logout' : ''}
        >
          <FiLogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-sm font-medium truncate">Logout</span>
          )}
        </button>
      </div>

      {renderLogoutModal()}
    </div>
  );
}
